import { Chromosome } from '../types/algorithm';
import { calculateChromosomeFitness } from '../utils/fitnessCalculator';

/**
 * Genetic Algorithm implementation from scratch
 * Based on standard GA principles: Selection, Crossover, Mutation, Elitism
 */

/**
 * Creates initial population
 */
export function initializePopulation(
  populationSize: number,
  taskCount: number,
  serviceCount: number
): Chromosome[] {
  const population: Chromosome[] = [];

  for (let i = 0; i < populationSize; i++) {
    const chromosome: Chromosome = {
      x: new Array(taskCount),
      fitness: 0,
      rwp: 0,
    };

    // Random initialization
    for (let j = 0; j < taskCount; j++) {
      chromosome.x[j] = Math.floor(Math.random() * serviceCount);
    }

    population.push(chromosome);
  }

  return population;
}

/**
 * Tournament selection (works with non-positive fitness)
 */
export function tournamentSelection(
  population: Chromosome[],
  tournamentSize: number = 3
): number {
  let bestIdx = Math.floor(Math.random() * population.length);
  let bestFitness = population[bestIdx].fitness;

  for (let i = 1; i < tournamentSize; i++) {
    const candidateIdx = Math.floor(Math.random() * population.length);
    if (population[candidateIdx].fitness > bestFitness) {
      bestIdx = candidateIdx;
      bestFitness = population[candidateIdx].fitness;
    }
  }

  return bestIdx;
}

/**
 * Calculates relative weight probability for roulette wheel selection
 * (Kept for compatibility with other algorithms)
 */
export function calculateRWP(population: Chromosome[]): void {
  let totalFitness = 0;

  // Calculate total fitness
  for (const chrom of population) {
    totalFitness += chrom.fitness;
  }

  // Avoid division by zero
  if (totalFitness === 0) {
    const equalProb = 1 / population.length;
    for (const chrom of population) {
      chrom.rwp = equalProb;
    }
    return;
  }

  // Calculate relative probabilities
  for (const chrom of population) {
    chrom.rwp = chrom.fitness / totalFitness;
  }
}

/**
 * Roulette wheel selection
 * (Kept for compatibility with other algorithms)
 */
export function rouletteWheelSelection(population: Chromosome[]): number {
  const r = Math.random();
  let sum = 0;

  for (let i = 0; i < population.length; i++) {
    sum += population[i].rwp;
    if (sum >= r) {
      return i;
    }
  }

  // Fallback to last element
  return population.length - 1;
}

/**
 * Single-point crossover
 * Creates two offspring from two parents
 */
export function singlePointCrossover(
  parent1: Chromosome,
  parent2: Chromosome,
  taskCount: number
): [Chromosome, Chromosome] {
  // Random crossover point
  const point = Math.floor(Math.random() * (taskCount - 1)) + 1;

  const child1: Chromosome = {
    x: [...parent1.x.slice(0, point), ...parent2.x.slice(point)],
    fitness: 0,
    rwp: 0,
  };

  const child2: Chromosome = {
    x: [...parent2.x.slice(0, point), ...parent1.x.slice(point)],
    fitness: 0,
    rwp: 0,
  };

  return [child1, child2];
}

/**
 * Mutation operator - random gene mutation
 */
export function mutate(
  chromosome: Chromosome,
  taskCount: number,
  serviceCount: number
): Chromosome {
  const mutated: Chromosome = {
    x: [...chromosome.x],
    fitness: 0,
    rwp: 0,
  };

  // Random mutation point (any gene can be mutated)
  const point = Math.floor(Math.random() * taskCount);
  mutated.x[point] = Math.floor(Math.random() * serviceCount);

  return mutated;
}

/**
 * Main GA evolution step
 * Uses generational replacement with elitism
 */
export function evolveGA(
  population: Chromosome[],
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  selectionRate: number,
  crossoverRate: number,
  mutationRate: number
): void {
  const popSize = population.length;
  
  // Sort population by fitness (best first) for elitism
  population.sort((a, b) => b.fitness - a.fitness);
  
  // Elitism: keep top 10% (or at least 1)
  const eliteCount = Math.max(1, Math.floor(popSize * 0.1));
  const newPopulation: Chromosome[] = [];
  
  // Copy elite individuals
  for (let i = 0; i < eliteCount; i++) {
    newPopulation.push({
      x: [...population[i].x],
      fitness: population[i].fitness,
      rwp: 0,
    });
  }
  
  // Generate offspring to fill remaining slots
  while (newPopulation.length < popSize) {
    // Selection: use tournament selection with selectionRate probability
    let parent1: Chromosome;
    let parent2: Chromosome;
    
    if (Math.random() < selectionRate / 100) {
      // Use tournament selection
      const idx1 = tournamentSelection(population);
      const idx2 = tournamentSelection(population);
      parent1 = population[idx1];
      parent2 = population[idx2];
    } else {
      // Random selection
      const idx1 = Math.floor(Math.random() * popSize);
      const idx2 = Math.floor(Math.random() * popSize);
      parent1 = population[idx1];
      parent2 = population[idx2];
    }
    
    // Crossover: apply with crossoverRate probability
    let child1: Chromosome;
    let child2: Chromosome;
    
    if (Math.random() < crossoverRate / 100) {
      [child1, child2] = singlePointCrossover(parent1, parent2, taskCount);
    } else {
      // No crossover, children are copies of parents
      child1 = { x: [...parent1.x], fitness: 0, rwp: 0 };
      child2 = { x: [...parent2.x], fitness: 0, rwp: 0 };
    }
    
    // Evaluate offspring
    calculateChromosomeFitness(child1, matrix, taskCount, serviceCount);
    calculateChromosomeFitness(child2, matrix, taskCount, serviceCount);
    
    // Mutation: apply with mutationRate probability per child
    if (Math.random() < mutationRate / 100) {
      child1 = mutate(child1, taskCount, serviceCount);
      calculateChromosomeFitness(child1, matrix, taskCount, serviceCount);
    }
    
    if (Math.random() < mutationRate / 100) {
      child2 = mutate(child2, taskCount, serviceCount);
      calculateChromosomeFitness(child2, matrix, taskCount, serviceCount);
    }
    
    // Add children to new population
    newPopulation.push(child1);
    if (newPopulation.length < popSize) {
      newPopulation.push(child2);
    }
  }
  
  // Replace old population with new generation
  for (let i = 0; i < popSize; i++) {
    population[i] = newPopulation[i];
  }
}

/**
 * Finds best chromosome in population
 */
export function findBestChromosome(population: Chromosome[]): Chromosome {
  let best = population[0];
  for (const chrom of population) {
    if (chrom.fitness > best.fitness) {
      best = chrom;
    }
  }
  return best;
}

/**
 * Runs GA for specified generations
 */
export async function runGeneticAlgorithm(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
    selectionRate: number;
    crossoverRate: number;
    mutationRate: number;
  },
  onGenerationComplete?: (generation: number, bestFitness: number) => void
): Promise<Chromosome> {
  // Initialize population
  let population = initializePopulation(
    parameters.populationSize,
    taskCount,
    serviceCount
  );

  // Evaluate initial population
  for (const chrom of population) {
    calculateChromosomeFitness(chrom, matrix, taskCount, serviceCount);
  }

  // Evolution loop
  let lastUpdateTime = Date.now();
  const UPDATE_INTERVAL = 100; // Update UI every 100ms (PERFORMANCE OPTIMIZATION)
  
  for (let gen = 0; gen < parameters.generations; gen++) {
    evolveGA(
      population,
      taskCount,
      serviceCount,
      matrix,
      parameters.selectionRate,
      parameters.crossoverRate,
      parameters.mutationRate
    );

    // Batch UI updates - only update every 100ms or every 10 generations (PERFORMANCE OPTIMIZATION)
    const now = Date.now();
    const shouldUpdate = (now - lastUpdateTime > UPDATE_INTERVAL) || (gen % 10 === 9) || (gen === parameters.generations - 1);
    
    if (shouldUpdate) {
      const best = findBestChromosome(population);
      if (onGenerationComplete) {
        onGenerationComplete(gen + 1, best.fitness);
      }
      lastUpdateTime = now;
      // Yield control to allow UI updates
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }

  return findBestChromosome(population);
}

